'use client'

import MiniCartButton from '@/feature/mini-cart/components/mini-cart-button/MiniCartButton.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Container } from '@/ui/layouts/container/Container'
import { Overlay } from '@/ui/layouts/overlay/Overlay'
import { Logo } from '@/ui/logos/logo/Logo.client'
import { Skin } from '@/ui/skin/Skin'
import type {
  HeaderButtonsV2_ConfigFragment,
  MenuEntryFragment,
  SignedInPointOfSaleButton_PickupPointOfSaleFragment,
} from '@gql-generated/graphql'
import { SearchInputWithDataLoader } from 'app/(web)/(marketing)/_components/SearchInputWithDataLoader.client'
import { usePathname } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { useResizeObserver } from 'usehooks-ts'
import { useHeaderScroll } from '../../hooks/useHeaderScroll'
import {
  BurgerMenuButton,
  FavoritesButton,
  PointOfSaleButton,
  SignedInAccountButton,
  SignedOutAccountButton,
  SignedOutCartButton,
} from './_components/HeaderButtons.client'
import { SliderTabs } from './_components/SliderTabs.client'

const BRAND = process.env.NEXT_PUBLIC_BRAND

function FlexSpacer({ className }: WithClassName) {
  return <div className={twMerge(className, 'flex-grow')} role="none" />
}

export type HeaderProps = {
  /** The ID of the selected pickup point of sale given by server side */
  pickUpPointOfSaleId?: string | null

  /** The config of user session point of sale */
  config: HeaderButtonsV2_ConfigFragment

  pickupPointOfSale?: SignedInPointOfSaleButton_PickupPointOfSaleFragment | null
  hasMoreThanOnePointOfSale: boolean

  /** The banner component */
  banner: React.ReactElement | null

  /** The menu entry to display on the menu */
  menuEntries: MenuEntryFragment[] | null | undefined
}

export function FeatureHeader({
  banner,
  menuEntries,
  pickupPointOfSale,
  hasMoreThanOnePointOfSale,
  config,
}: HeaderProps): React.ReactElement {
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null)

  const handleMenuPopinClose = useCallback(() => {
    setSelectedCategoryKey(null)
  }, [])

  const pathname = usePathname()

  const [tabContentVisible, setTabContentVisible] = useState(pathname === '/')

  const headerRef = useRef<HTMLDivElement>(null)
  const headerSizeRef = useRef<HTMLDivElement>(null)

  const { height: liveHeaderHeight = 0 } = useResizeObserver({
    // @ts-expect-error https://github.com/juliencrn/usehooks-ts/issues/602
    ref: headerSizeRef,
  })

  const [isMenuOpen, setIsMenuOpen] = useState(pathname === '/')
  useHeaderScroll(headerRef, liveHeaderHeight, isMenuOpen, setTabContentVisible, pathname)

  const isSignedIn = useIsAuthenticated()

  const handleShowMenu = useCallback((isOpen: boolean) => {
    setIsMenuOpen(isOpen)

    if (isOpen) {
      setTabContentVisible(true)
    }
  }, [])

  return (
    <header className="group/header print:hidden" ref={headerRef}>
      <div
        ref={headerSizeRef}
        className="fixed left-0 top-0 z-header flex w-screen flex-col bg-white pr-[--app-scrollbar-dx] opacity-100 shadow-xs transition duration-300 ease-in-out group-data-[scrolled-beyond]/header:-translate-y-[var(--app-header-height)] group-data-[scrolled-beyond]/header:shadow-none group-data-[scrolled-beyond]/header:desktop:-translate-y-[var(--app-header-height-desktop)] group-data-[scrolled-beyond]/header:desktop:opacity-0"
      >
        {banner}

        <Skin
          tint="body"
          className="relative flex w-full justify-between pb-2 desktop:py-2 desktop:pb-0"
        >
          <Container>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-start gap-0 desktop:flex-nowrap desktop:gap-1">
                <div className="flex items-center gap-2 desktop:gap-3">
                  <div className="flex h-7 items-center desktop:h-auto">
                    <Logo
                      size={BRAND === 'destil_nl' || BRAND === 'dexis_nl' ? 'l' : 's'}
                      className="order-first max-w-fit shrink-0 grow-0 text-white"
                      onPress={() => {
                        pushToGtmDataLayer({
                          event: 'gaEvent',
                          eventCategory: 'header',
                          eventAction: 'logo',
                          eventLabel: 'clic logo',
                        })
                      }}
                    />
                  </div>
                </div>
                <FlexSpacer className="desktop:hidden" />

                <SearchInputWithDataLoader className="z-header order-last w-full min-w-16 grow basis-full self-start pt-1 desktop:order-none desktop:ml-4 desktop:mr-2 desktop:min-w-20 desktop:flex-1" />

                <div className="-mr-1 flex items-center desktop:mr-0 desktop:min-w-0">
                  <PointOfSaleButton
                    config={config}
                    isSignedIn={isSignedIn}
                    pickupPointOfSale={pickupPointOfSale}
                    hasMoreThanOnePointOfSale={hasMoreThanOnePointOfSale}
                    className="desktop:order-none desktop:mt-0 desktop:min-w-[auto] desktop:basis-auto"
                  />

                  {isSignedIn && <FavoritesButton className="shrink-0" />}

                  {isSignedIn ? (
                    <SignedInAccountButton className="shrink-0" />
                  ) : (
                    <SignedOutAccountButton className="shrink-0" />
                  )}

                  {isSignedIn ? (
                    <MiniCartButton className="desktop:ml-3" />
                  ) : (
                    <SignedOutCartButton className="shrink-0" />
                  )}

                  <BurgerMenuButton />
                </div>
              </div>

              <SliderTabs
                isMenuOpen={isMenuOpen}
                tabContentVisible={tabContentVisible}
                selectedCategoryKey={selectedCategoryKey}
                setSelectedCategoryKey={setSelectedCategoryKey}
                handleShowMenu={handleShowMenu}
                handleMenuPopinClose={handleMenuPopinClose}
                menuEntries={menuEntries}
              />
            </div>
          </Container>
        </Skin>
      </div>
      <Overlay
        isVisible={selectedCategoryKey !== null}
        className="fixed inset-0 z-header-overlay hidden desktop:block"
        onClick={handleMenuPopinClose}
      />
    </header>
  )
}
